import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout, retry } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '@app/app-config.module';
import {
  StandardResponse,
  StandardResponses,
} from '@app/core/models/standard-response.model';
import {
  CreateSigningHubUserRequest,
  SearchSigningHubUser,
  SigningHubTranslateLanguage,
  SigningHubUser,
  SigningHubUserResult,
} from '@app/core/models/signing-hub-user.model';
import {
  CreateSigningHubCompanyRequest,
  SearchSigningHubCompany,
  SigningHubCompany,
  SigningHubCompanyResult,
  UpdateSigningHubCompanySetting,
} from '../models/signing-hub-company.model';
import {
  DeleteSigningHubCertificateUser,
  DeleteSigningHubESealCertificate,
  GetSigningHubCertificate,
  GetSigningHubESealCertificate,
  SearchSigningHubESealCertificate,
  SetSigningHubESealCertificate,
  SigningHubCertificateDetail,
  SigningHubESealCertificate,
  UploadSigningHubESealCertificate,
} from '../models/signing-hub-eseal-certificate.model';
import { UploadSigningHubUserCertificate } from '../models/signing-hub-user-certificate.model';
import {
  SearchSigningHubUserCertificate,
  SigningHubUserCertificate,
} from '@app/core/models/signing-hub-user-certificate.model';

@Injectable({
  providedIn: 'root',
})
export class SigningHubService {
  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private http: HttpClient
  ) {}

  getUser(uid: string): Observable<StandardResponse<SigningHubUser>> {
    return this.http
      .post<StandardResponse<SigningHubUser>>(
        `${this.config.apiEndpoint}/manage/signing-hub/get-user`,
        { uid: uid }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getUsers(
    criteria: SearchSigningHubUser,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<SigningHubUserResult>> {
    return this.http
      .post<StandardResponses<SigningHubUserResult>>(
        `${this.config.apiEndpoint}/manage/signing-hub/search-user`,
        {
          criteria: criteria,
          pageNumber: pageNumber,
          pageSize: pageSize,
        }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getCompany(cpid: string): Observable<StandardResponse<SigningHubCompany>> {
    return this.http
      .post<StandardResponse<SigningHubCompany>>(
        `${this.config.apiEndpoint}/manage/signing-hub/get-signing-hub-company-integration`,
        { cpid: cpid }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getCompanies(
    criteria: SearchSigningHubCompany,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<SigningHubCompanyResult>> {
    return this.http
      .post<StandardResponses<SigningHubCompanyResult>>(
        `${this.config.apiEndpoint}/manage/signing-hub/search-signing-hub-company-integration`,
        {
          criteria: criteria,
          pageNumber: pageNumber,
          pageSize: pageSize,
        }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  updateCompanySetting(
    update: UpdateSigningHubCompanySetting
  ): Observable<StandardResponse<SigningHubCompany>> {
    return this.http
      .post<StandardResponse<SigningHubCompany>>(
        `${this.config.apiEndpoint}/manage/signing-hub/update-signing-hub-company-integration`,
        update
      )
      .pipe(timeout(this.config.apiTimeout));
  }

  createSigningHubCompany(
    request: CreateSigningHubCompanyRequest
  ): Observable<StandardResponse<SigningHubCompany>> {
    return this.http
      .post<StandardResponse<SigningHubCompany>>(
        `${this.config.apiEndpoint}/manage/signing-hub/create-signing-hub-company-integration`,
        request
      )
      .pipe(timeout(this.config.apiTimeout));
  }

  createSigningHubUser(
    request: CreateSigningHubUserRequest
  ):Observable<StandardResponse<SigningHubUser>> {
    return this.http
    .post<StandardResponse<SigningHubUser>>(
      `${this.config.apiEndpoint}/manage/signing-hub/create-user`,
      request
    )
    .pipe(timeout(this.config.apiTimeout));
  }



  getUserCertificates(
    criteria: SearchSigningHubUserCertificate,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<SigningHubUserCertificate>> {
    return this.http
      .post<StandardResponses<SigningHubUserCertificate>>(
        `${this.config.apiEndpoint}/manage/signing-hub/search-signing-hub-certificate-user`,
        {
          criteria: criteria,
          pageNumber: pageNumber,
          pageSize: pageSize,
        }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  translateLanguage(
    request: SigningHubTranslateLanguage,
    cpid: string
  ):Observable<StandardResponse<void>> {
    const httpOptions = {
      headers: new HttpHeaders({
        cpid: cpid,
      }),
    };
    return this.http
    .post<StandardResponse<void>>(
      `${this.config.apiEndpoint}/manage/signing-hub/translate-language`,
      request,
      httpOptions
    )
    .pipe(timeout(this.config.apiTimeout));
  }

  // addCompanyCertificate(
  //   certificate: AddSigningHubCompanyCertificate
  // ): Observable<StandardResponse<void>> {
  //   return this.http
  //     .post<StandardResponse<void>>(
  //       `${this.config.apiEndpoint}/manage/signing-hub/add-signing-hub-certificate-company`,
  //       certificate
  //     )
  //     .pipe(timeout(this.config.apiTimeout));
  // }

  // deleteCompanyCertificate(
  //   certificate: DeleteSigningHubCompanyCertificate
  // ): Observable<StandardResponse<void>> {
  //   return this.http
  //     .post<StandardResponse<void>>(
  //       `${this.config.apiEndpoint}/manage/signing-hub/delete-signing-hub-certificate-company`,
  //       certificate
  //     )
  //     .pipe(timeout(this.config.apiTimeout));
  // }

  getPKCS12Slot(
    base64P12: string,
    password: string
  ): Observable<StandardResponse<string[]>> {
    return this.http
      .post<StandardResponse<string[]>>(
        `${this.config.apiEndpoint}/manage/signing-hub/get-certificate-slot-name`,
        { base64P12: base64P12, password: password }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getRootCertificates(): Observable<StandardResponse<string[]>> {
    return this.http
      .post<StandardResponse<string[]>>(
        `${this.config.apiEndpoint}/manage/signing-hub/get-root-certificates`,
        {}
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  uploadUserCertificate(certificate: UploadSigningHubUserCertificate) {
    return this.http.post<StandardResponse<void>>(
      `${this.config.apiEndpoint}/manage/signing-hub/add-signing-hub-certificate-user`,
      certificate
    );
  }

  uploadESealCertificate(certificate: UploadSigningHubESealCertificate) {
    return this.http.post<StandardResponse<SigningHubESealCertificate>>(
      `${this.config.apiEndpoint}/manage/signing-hub/create-signing-hub-eseal`,
      certificate
    );
  }

  getESealCertificate(criteria: GetSigningHubESealCertificate) {
    return this.http
      .post<StandardResponse<SigningHubESealCertificate>>(
        `${this.config.apiEndpoint}/manage/signing-hub/get-signing-hub-eseal`,
        criteria
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  getESealCertificates(
    criteria: SearchSigningHubESealCertificate,
    pageNumber: number,
    pageSize: number
  ): Observable<StandardResponses<SigningHubESealCertificate>> {
    return this.http
      .post<StandardResponses<SigningHubESealCertificate>>(
        `${this.config.apiEndpoint}/manage/signing-hub/search-signing-hub-eseal`,
        {
          criteria: criteria,
          pageNumber: pageNumber,
          pageSize: pageSize,
        }
      )
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiTimeout));
  }

  setESealCertificate(certificate: SetSigningHubESealCertificate) {
    return this.http
      .post<StandardResponse<SigningHubESealCertificate>>(
        `${this.config.apiEndpoint}/manage/signing-hub/change-signing-hub-eseal`,
        certificate
      )
      .pipe(retry(this.config.apiRetry));
  }

  deleteESealCertificate(certificate: DeleteSigningHubESealCertificate) {
    return this.http
      .post<StandardResponse<void>>(
        `${this.config.apiEndpoint}/manage/signing-hub/delete-signing-hub-eseal`,
        certificate
      )
      .pipe(retry(this.config.apiRetry));
  }

  deleteSigningHubCertificateUser(certificate: DeleteSigningHubCertificateUser) {
    return this.http
      .post<StandardResponse<void>>(
        `${this.config.apiEndpoint}/manage/signing-hub/delete-signing-hub-certificate-user`,
        certificate
      )
      .pipe(retry(this.config.apiRetry));
  }

  ///// 
  getSigningHubCertificateUserDetail(certificate: SigningHubCertificateDetail) {
    return this.http
      .post<StandardResponse<GetSigningHubCertificate>>(
        `${this.config.apiEndpoint}/manage/siging-hub/get-signing-hub-certificate-user-information`,
        certificate
      )
      .pipe(retry(this.config.apiRetry));
  }


  
  
}
